/**
 * 选中框 / 手柄 / 框选矩形 —— 全在 **HTML 覆盖层**，绝不往 <svg> 里插节点
 * （插进去的样式会被序列化进保存结果，这是 dongTai 踩过的坑）。
 *
 * 定位一律用 getBoundingClientRect 差值：它已把 CSS 缩放与元素自身 transform 算进去，
 * 所以缩放画布后依然贴合。坐标换算是纯函数，可单测。
 */

export interface Frame {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const HANDLES: readonly HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** 元素屏幕矩形 → 相对宿主容器的覆盖层坐标 */
export const frameFromRects = (el: DOMRect, host: DOMRect): Frame => ({
  left: el.left - host.left,
  top: el.top - host.top,
  width: Math.max(el.width, 2),
  height: Math.max(el.height, 2),
});

/** 手柄锚点（相对 frame 的百分比） */
export const HANDLE_ANCHOR: Record<HandleId, { x: number; y: number; cursor: string }> = {
  nw: { x: 0, y: 0, cursor: 'nwse-resize' },
  n: { x: 0.5, y: 0, cursor: 'ns-resize' },
  ne: { x: 1, y: 0, cursor: 'nesw-resize' },
  e: { x: 1, y: 0.5, cursor: 'ew-resize' },
  se: { x: 1, y: 1, cursor: 'nwse-resize' },
  s: { x: 0.5, y: 1, cursor: 'ns-resize' },
  sw: { x: 0, y: 1, cursor: 'nesw-resize' },
  w: { x: 0, y: 0.5, cursor: 'ew-resize' },
};

export interface Overlay {
  readonly root: HTMLElement;
  /** 重画选中框；传入空数组即隐藏 */
  setFrames(frames: Frame[], opts?: { showHandles?: boolean }): void;
  /** 临时预览框（悬停高亮用）：**不动选中集**，只画框；null 即清空 */
  setPreview(frames: Frame[] | null): void;
  /** 框选矩形（拖动中实时更新） */
  setMarquee(frame: Frame | null): void;
  destroy(): void;
}

export function createOverlay(host: HTMLElement): Overlay {
  const root = document.createElement('div');
  root.className = 'sve-overlay';
  host.appendChild(root);

  const marquee = document.createElement('div');
  marquee.className = 'sve-marquee';
  marquee.hidden = true;
  root.appendChild(marquee);

  // 预览层单独一个容器：与真实选中框互不干扰（清预览不会动到选中框）
  const preview = document.createElement('div');
  preview.className = 'sve-preview';
  root.appendChild(preview);

  const paint = (box: HTMLElement, f: Frame): void => {
    box.style.left = `${f.left}px`;
    box.style.top = `${f.top}px`;
    box.style.width = `${f.width}px`;
    box.style.height = `${f.height}px`;
  };

  return {
    root,
    setFrames(frames, opts = {}) {
      root.querySelectorAll('.sve-frame').forEach((n) => n.remove());
      for (const f of frames) {
        const box = document.createElement('div');
        box.className = 'sve-frame';
        paint(box, f);
        if (opts.showHandles !== false) {
          const rot = document.createElement('span');
          rot.className = 'sve-handle sve-handle-rot';
          rot.dataset.rotate = '1';
          rot.title = '旋转（拖拽，Shift 吸附 15°）';
          rot.style.left = 'calc(50% - 5px)';
          rot.style.top = '-24px';
          rot.style.cursor = 'grab';
          box.appendChild(rot);
          for (const id of HANDLES) {
            const a = HANDLE_ANCHOR[id];
            const h = document.createElement('span');
            h.className = `sve-handle sve-handle-${id}`;
            h.dataset.handle = id;
            h.style.left = `calc(${a.x * 100}% - 4px)`;
            h.style.top = `calc(${a.y * 100}% - 4px)`;
            h.style.cursor = a.cursor;
            box.appendChild(h);
          }
        }
        root.appendChild(box);
      }
    },
    setPreview(frames) {
      preview.textContent = '';
      for (const f of frames ?? []) {
        const box = document.createElement('div');
        box.className = 'sve-frame sve-frame-preview';
        paint(box, f);
        preview.appendChild(box);
      }
    },
    setMarquee(frame) {
      if (!frame) {
        marquee.hidden = true;
        return;
      }
      marquee.hidden = false;
      marquee.style.left = `${frame.left}px`;
      marquee.style.top = `${frame.top}px`;
      marquee.style.width = `${frame.width}px`;
      marquee.style.height = `${frame.height}px`;
    },
    destroy() {
      if (root.parentNode === host) host.removeChild(root);
    },
  };
}
